import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, MARKS } from "@contentful/rich-text-types";
import { richContentOptions } from "@/components/content-options";
import Link from "next/link";

import { useState } from "react";
import { currency } from "@/utils/formats";
import { useSession } from "@/components/providers/session";
import PartnerModal, { PartnerData } from "@/components/elearning/partner-modal";

const Bold = (text) => (
  <strong className="font-medium text-black">{text}</strong>
);

const Heading6 = (node, children) => (
  <h6 className="text-sm font-medium text-brand-600">{children}</h6>
);

const P = (node, children) => (
  <p className="first:flex-1 mt-3 first:mt-0 text-gray-500 text-sm lg:text-sm">
    {children}
  </p>
);

export default function OnlineCoursesListing({ courses, ...rest }) {
  const { addOnlineCourses, setContext } = useSession();
  const [partner, setPartner] = useState<PartnerData | null>(null);

  const packages = courses.items.filter((item) => item.fields.package === true);
  const nonPackages = courses.items.filter((item) => item.fields.package !== true);

  console.log(packages);
  console.log(nonPackages);

  return (
    <>
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 sm:py-8 lg:max-w-7xl lg:px-8">
        <h3 className="text-2xl font-bold text-gray-900 lg:text-3xl">Online Training Packages</h3>
        <div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-8 sm:grid-cols-2 sm:gap-y-10 lg:grid-cols-3">
          {packages.map((product) => {
            const price = product.fields.pricing
              ? product.fields.pricing.unit_amount
              : product.fields.link && product.fields.link.line_items
              ? product.fields.link.line_items.data[0].price.amount_total
              : product.fields.data?.price
              ? Number(product.fields.data?.price) * 100
              : null;

            return (
              <div
                key={product.fields.listingImage.fields.file.url}
                className="group relative flex flex-col"
              >
                <div className="absolute right-1 top-1 bg-brand-600  text-white rounded-full py-1 px-2 shadow shadow-gray-500 z-10">
                  <p className="text-sm">Online Course</p>
                </div>
                <div className="relative">
                  <img
                    alt={product.fields.listingImage.fields.title}
                    src={product.fields.listingImage.fields.file.url}
                    className="aspect-[4/3] w-full rounded-lg bg-gray-100 object-cover"
                  />
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 flex items-end p-4 opacity-0 group-hover:opacity-100"
                  >
                    <div className="w-full rounded-md bg-white/75 px-4 py-2 text-center text-sm font-medium text-gray-900 backdrop-blur backdrop-filter">
                      View Course
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-base font-medium text-gray-900 flex-1">
                  <h3 className="font-semibold text-lg">
                    <a href={product.fields.url}>
                      <span aria-hidden="true" className="absolute inset-0" />
                      {product.fields.title}
                    </a>
                  </h3>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="font-semibold text-brand-600 text-base">
                    {price ? currency(price) : `£${product.fields.data.full}`}
                  </p>
                  {product.fields.data.was ? (
                    <p className="text-sm">
                      Was: {currency(product.fields.data.was)}
                    </p>
                  ) : null}
                </div>
                <p className="sr-only">{product.fields.type}</p>
                {product.fields.listingText && (
                  <div className="sr-only">
                    {documentToReactComponents(
                      product.fields.listingText,
                      options
                    )}
                  </div>
                )}
                <div className="mt-5 w-full">
                  {product.fields.data && product.fields.data.partner ? (
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        setPartner(product.fields.data);
                      }}
                      className="cursor-pointer block w-full rounded-md bg-brand-600 px-6 py-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 outline outline-2 outline-offset-2 outline-brand-600 gradient-border transition-colors duration-200 hover:outline-brand-500 text-center"
                    >
                      <span className="relative z-[1] text-center">
                        Buy Course
                      </span>
                    </a>
                  ) : (
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        addOnlineCourses(product, {
                          online: true,
                        });
                        setContext((s) => ({
                          ...s,
                          onlineBasket: !s.onlineBasket,
                        }));
                      }}
                      className="cursor-pointer block w-full rounded-md bg-brand-600 px-6 py-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 outline outline-2 outline-offset-2 outline-brand-600 gradient-border transition-colors duration-200 hover:outline-brand-500 text-center"
                    >
                      <span className="relative z-[1] text-center">
                        Buy Course
                      </span>
                    </a>
                  )}
                </div>
                <div className="mt-5 w-full">
                  <Link
                    href="/request-a-callback"
                    className="w-full block group bg-white rounded-md text-sm font-semibold px-6 py-4 leading-6 eading-6 text-gray-600 ring-2 ring-gray-900/10 hover:ring-brand-600 shadow-lg  transition-all duration-200 hover:shadow-lg hover:shadow-brand-200 text-center"
                  >
                    View course details{" "}
                    <span
                      aria-hidden="true"
                      className="group-hover:pl-1 transition-all duration-150"
                    >
                      →
                    </span>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mt-12 lg:text-3xl">Online Training Courses</h3>
        <div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-8 sm:grid-cols-2 sm:gap-y-10 lg:grid-cols-3">
          {nonPackages.map((product) => {
            const price = product.fields.pricing
              ? product.fields.pricing.unit_amount
              : product.fields.link && product.fields.link.line_items
              ? product.fields.link.line_items.data[0].price.amount_total
              : product.fields.data?.price
              ? Number(product.fields.data?.price) * 100
              : null;

            return (
              <div
                key={product.fields.listingImage.fields.file.url}
                className="group relative flex flex-col"
              >
                <div className="absolute right-1 top-1 bg-brand-600  text-white rounded-full py-1 px-2 shadow shadow-gray-500 z-10">
                  <p className="text-sm">Online Course</p>
                </div>
                <div className="relative">
                  <img
                    alt={product.fields.listingImage.fields.title}
                    src={product.fields.listingImage.fields.file.url}
                    className="aspect-[4/3] w-full rounded-lg bg-gray-100 object-cover"
                  />
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 flex items-end p-4 opacity-0 group-hover:opacity-100"
                  >
                    <div className="w-full rounded-md bg-white/75 px-4 py-2 text-center text-sm font-medium text-gray-900 backdrop-blur backdrop-filter">
                      View Course
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-base font-medium text-gray-900 flex-1">
                  <h3 className="font-semibold text-lg">
                    <a href={product.fields.url}>
                      <span aria-hidden="true" className="absolute inset-0" />
                      {product.fields.title}
                    </a>
                  </h3>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="font-semibold text-brand-600 text-base">
                    {price ? currency(price) : `£${product.fields.data.full}`}
                  </p>
                  {product.fields.data.was ? (
                    <p className="text-sm">
                      Was: {currency(product.fields.data.was)}
                    </p>
                  ) : null}
                </div>
                <p className="sr-only">{product.fields.type}</p>
                {product.fields.listingText && (
                  <div className="sr-only">
                    {documentToReactComponents(
                      product.fields.listingText,
                      options
                    )}
                  </div>
                )}
                <div className="mt-5 w-full">
                  {product.fields.data && product.fields.data.partner ? (
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        setPartner(product.fields.data);
                      }}
                      className="cursor-pointer block w-full rounded-md bg-brand-600 px-6 py-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 outline outline-2 outline-offset-2 outline-brand-600 gradient-border transition-colors duration-200 hover:outline-brand-500 text-center"
                    >
                      <span className="relative z-[1] text-center">
                        Buy Course
                      </span>
                    </a>
                  ) : (
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        addOnlineCourses(product, {
                          online: true,
                        });
                        setContext((s) => ({
                          ...s,
                          onlineBasket: !s.onlineBasket,
                        }));
                      }}
                      className="cursor-pointer block w-full rounded-md bg-brand-600 px-6 py-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 outline outline-2 outline-offset-2 outline-brand-600 gradient-border transition-colors duration-200 hover:outline-brand-500 text-center"
                    >
                      <span className="relative z-[1] text-center">
                        Buy Course
                      </span>
                    </a>
                  )}
                </div>
                <div className="mt-5 w-full">
                  <Link
                    href="/request-a-callback"
                    className="w-full block group bg-white rounded-md text-sm font-semibold px-6 py-4 leading-6 eading-6 text-gray-600 ring-2 ring-gray-900/10 hover:ring-brand-600 shadow-lg  transition-all duration-200 hover:shadow-lg hover:shadow-brand-200 text-center"
                  >
                    View course details{" "}
                    <span
                      aria-hidden="true"
                      className="group-hover:pl-1 transition-all duration-150"
                    >
                      →
                    </span>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {partner && (
        <PartnerModal data={partner} onClose={() => setPartner(null)} />
      )}
    </>
  );
}

const options = {
  ...richContentOptions,
  renderMark: {
    ...richContentOptions.renderMark,
    [MARKS.BOLD]: Bold,
  },
  renderNode: {
    ...richContentOptions.renderNode,
    [BLOCKS.HEADING_6]: Heading6,
    [BLOCKS.PARAGRAPH]: P,
  },
};
